/**
 * Video embed — Phase 4c block-level atom (wiki feature plan §4c).
 *
 * Renders a single `<video controls preload="metadata">` against a
 * presigned GET URL for an attachment row uploaded through the
 * FU-D presign flow (BE `parentKind='wiki-video'`, 200 MB cap, MIME
 * mp4/webm/mov per the Phase 4c BE allowlist).
 *
 * Schema shape — block-level atom:
 *   - `group: 'block'` + `atom: true`
 *   - `selectable: true`, `draggable: true`, `marks: ''`
 *
 * Block-atom keyboard behavior: Enter on selected video inserts a
 * paragraph after the node (per UX Agent 3 §5 — block atoms need an
 * explicit "exit downward" affordance). Implemented via
 * `addKeyboardShortcuts`.
 *
 * Attributes:
 *   - `fileId: string | null` — attachment row UUID.
 *   - `mimeType: string | null` — informational only (the `<video>` tag
 *     sniffs by URL/content); persisted so the NodeView can render a
 *     restricted-codec message ahead of a failed load.
 *   - `caption: string` — optional caption rendered under the video.
 *     Static text (the node is atom, NOT contenteditable — caption
 *     edits happen via an inline `<input>` in the NodeView).
 *   - `uploadingPlaceholderId` (transient) — client-side id the OS-drop
 *     upload flow uses to locate this node once the upload settles
 *     (mirrors the fileEmbed attr of the same name). Never round-trips
 *     through HTML.
 *   - `uploadingFileName` (transient) — companion display name for the
 *     uploading panel; no attachment row exists yet to read one from.
 *
 * Round-trip:
 *   - parseHTML matches `div[data-block-type="video-embed"]`.
 *   - renderHTML emits `data-file-id`, `data-mime-type`, `data-caption`
 *     (caption omitted when empty).
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

export interface BuildVideoEmbedExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildVideoEmbedExtension({ view }: BuildVideoEmbedExtensionOptions) {
  return Node.create({
    name: 'videoEmbed',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,
    marks: '',

    addAttributes() {
      return {
        fileId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-file-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { fileId: string | null }) =>
            attrs.fileId ? { 'data-file-id': attrs.fileId } : {},
        },
        mimeType: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => el.getAttribute('data-mime-type'),
          renderHTML: (attrs: { mimeType: string | null }) =>
            attrs.mimeType ? { 'data-mime-type': attrs.mimeType } : {},
        },
        caption: {
          default: '',
          parseHTML: (el: HTMLElement) => el.getAttribute('data-caption') ?? '',
          renderHTML: (attrs: { caption: string }) =>
            attrs.caption ? { 'data-caption': attrs.caption } : {},
        },
        uploadingPlaceholderId: {
          // In-memory-only — the OS-drop upload flow uses it to locate
          // the node while the upload is in flight.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        uploadingFileName: {
          // In-memory-only — filename shown by the uploading panel
          // before any attachment row exists.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        uploadingFailed: {
          // In-memory-only — flips the uploading placeholder into the
          // error branch (retry / dismiss). Never serialized; the
          // flush-time strip removes the node via uploadingPlaceholderId.
          default: false,
          parseHTML: () => false,
          renderHTML: () => ({}),
        },
      }
    },

    parseHTML() {
      return [{ tag: 'div[data-block-type="video-embed"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', { ...HTMLAttributes, 'data-block-type': 'video-embed' }]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },

    addKeyboardShortcuts() {
      return {
        // Block-atom: Enter inserts a paragraph after the selected
        // node. Without this the user has no keyboard path out of the
        // atom — arrow-down works, but Enter is the universal
        // convention (Notion / Confluence behavior).
        Enter: ({ editor }) => {
          const { state } = editor
          const sel = state.selection
          // Only fire when the selected node IS this video embed.
          const node = sel.$from.nodeAfter
          if (!node || node.type.name !== 'videoEmbed') return false
          return editor
            .chain()
            .focus()
            .insertContentAt(sel.from + node.nodeSize, { type: 'paragraph' })
            .run()
        },
      }
    },
  })
}

export { isUuid as isVideoUuid }
